/**
 * E6 branding light-up hook (SPEC PX-11 R2 - AC8).
 *
 * Lights up the three E6 `tenant_branding` fields the portal does NOT yet
 * consume: `accent_color`, `favicon_url`, and `document.title` (from
 * `display_name`). `PortalLayout` already consumes `display_name` /
 * `primary_color` / `logo_url` / `client_label`; this hook adds the remaining
 * three. Additive wiring against E6's EXISTING `branding` payload on /api/me - no
 * new data, no new collection.
 *
 * TOKEN COORDINATION (FE-1 soft-gate): FE-1 (the metabus restyle) has NOT shipped
 * in this worktree, so the per-tenant brand override binds the CURRENT shadcn
 * `:root` tokens `--primary` / `--accent`. The light-up MECHANISM is identical
 * either way - only the CSS-var NAME the override binds moves - so this is
 * buildable regardless of FE-1 ordering. The override sets the CSS custom
 * properties on the layout root via inline style; a tenant with no branding falls
 * back to the stock token defaults (the override is simply absent).
 *
 * Colors are stored as hex (E6 `tenant_branding`). shadcn tokens are HSL triples,
 * but a hex value set on `--primary` still tints any element that reads
 * `hsl(var(--primary))`? No - so we expose BOTH: the raw hex on a brand-scoped
 * variable the layout reads directly, AND (defensively) leave the shadcn HSL
 * tokens untouched unless a tenant brand color is present, in which case we drive
 * the brand-primary / brand-accent CSS vars the portal shell reads. This avoids
 * corrupting the global shadcn `hsl(var(--primary))` consumers with a non-HSL
 * value.
 */

import { useEffect } from 'react'
import type { PortalBranding } from '@/types/auth'

/** The brand-scoped CSS variables the portal shell reads for the per-tenant
 * accent/primary override. Kept distinct from the global shadcn HSL tokens so a
 * hex brand color never corrupts `hsl(var(--primary))` consumers. */
export const BRAND_PRIMARY_VAR = '--brand-primary'
export const BRAND_ACCENT_VAR = '--brand-accent'

/**
 * Apply a tenant's branding light-up:
 *  - sets `document.title` from `display_name` (when present),
 *  - swaps the document favicon from `favicon_url` (when present),
 *  - returns the inline-style object the layout root spreads to bind the
 *    per-tenant `--brand-primary` / `--brand-accent` overrides (the soft-gate
 *    target; the current shadcn `--primary`/`--accent` are the fallback tokens
 *    when no brand color is set).
 *
 * Idempotent + degrades to stock defaults when `branding` is absent.
 */
export function useBrandingTheme(
  branding: PortalBranding | undefined
): React.CSSProperties {
  useEffect(() => {
    // document.title from display_name (light-up #3).
    if (branding?.display_name) {
      document.title = branding.display_name
    }
  }, [branding?.display_name])

  useEffect(() => {
    // favicon swap from favicon_url (light-up #2).
    if (!branding?.favicon_url) return
    const url = branding.favicon_url
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    const prev = link.href
    link.href = url
    return () => {
      // restore the prior favicon on unmount so leaving the portal reverts.
      if (link && prev) link.href = prev
    }
  }, [branding?.favicon_url])

  // accent_color light-up #1 (+ primary): bind the per-tenant brand vars. When a
  // brand color is absent the var is simply not set, so the shell falls back to
  // its stock token default. These map onto the soft-gate target tokens.
  const style: React.CSSProperties = {}
  if (branding?.primary_color) {
    ;(style as Record<string, string>)[BRAND_PRIMARY_VAR] = branding.primary_color
  }
  if (branding?.accent_color) {
    ;(style as Record<string, string>)[BRAND_ACCENT_VAR] = branding.accent_color
  }
  return style
}
